import { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { StrategyTemplatePickerModal } from "@/components/StrategyTemplatePickerModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import {
  ArrowLeft, ArrowRight, SpinnerGap, Target, Handshake, Megaphone,
  ArrowsClockwise, Phone, Download, ShareNetwork, Heart, BookOpen,
  Cursor, Plus, Lightning,
} from "@phosphor-icons/react";

const GOALS = [
  { id: "leads", label: "Generate leads or inquiries", icon: Target },
  { id: "trust", label: "Build trust and authority", icon: Handshake },
  { id: "promote", label: "Promote a specific offer or service", icon: Megaphone },
  { id: "nurture", label: "Nurture existing audience or clients", icon: ArrowsClockwise },
];

const OUTCOMES = [
  { id: "book_call", label: "Book a call or consultation", icon: Phone },
  { id: "download", label: "Download or sign up for something", icon: Download },
  { id: "share", label: "Share or engage with the content", icon: ShareNetwork },
  { id: "confidence", label: "Feel more confident about working with you", icon: Heart },
  { id: "understand", label: "Understand a concept or approach", icon: BookOpen },
  { id: "custom", label: "Take a specific action (custom)", icon: Cursor },
];

const CONTENT_TYPES = [
  "Newsletter", "LinkedIn Post", "Lead Magnet", "Instagram Post",
  "Blog Post", "Email Sequence", "Facebook Post", "Twitter/X Thread",
  "YouTube Script", "Poll",
];

const TONE_CHIPS = [
  "Direct", "Sharp", "Calm", "Contrarian", "Insightful", "Practical", "Urgent",
  "Warm", "Provocative", "Technical", "Empathetic", "Witty", "Authoritative", "Conversational",
];

const PROCESSING_MESSAGES = [
  "Analyzing your goal...",
  "Mapping to your audience...",
  "Building your content angle...",
  "Preparing your brief...",
];

const AUDIENCE_PROMPTS = [
  { chip: "+ Add their pain point", question: "What's their biggest pain point?" },
  { chip: "+ Add their goal", question: "What's the goal they're chasing?" },
  { chip: "+ Add their objection", question: "What's their main hesitation or objection?" },
];

export default function BriefBuilderPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const businessId = user?.active_business_id || "";

  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [processingMsg, setProcessingMsg] = useState(0);

  // Step 1
  const [goal, setGoal] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  // Step 2
  const [audience, setAudience] = useState("");
  const [activePrompt, setActivePrompt] = useState(null);
  const [promptAnswer, setPromptAnswer] = useState("");
  // Step 3
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [customOutcome, setCustomOutcome] = useState("");
  // Step 4
  const [contentType, setContentType] = useState("");
  const [platform, setPlatform] = useState("");
  const [toneOverride, setToneOverride] = useState("");
  // Step 5
  const [additionalContext, setAdditionalContext] = useState("");
  const [strategyProfiles, setStrategyProfiles] = useState([]);
  const [strategyProfileId, setStrategyProfileId] = useState("");
  const [defaultStrategyProfileId, setDefaultStrategyProfileId] = useState("");
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const effectiveGoal = goal === "custom" ? customGoal : GOALS.find(g => g.id === goal)?.label || goal;
  const effectiveOutcome = desiredOutcome === "custom" ? customOutcome : OUTCOMES.find(o => o.id === desiredOutcome)?.label || desiredOutcome;

  const loadStrategies = useCallback(async () => {
    try {
      const resp = await api.get("/strategy/profiles");
      setStrategyProfiles(resp.data?.profiles || []);
      const defaultId = resp.data?.default_strategy_profile_id || "";
      setDefaultStrategyProfileId(defaultId);
      if (defaultId) setStrategyProfileId(prev => prev || defaultId);
    } catch { /* silent */ }
  }, [api]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    if (prefill.goal) setGoal(prefill.goal);
    if (prefill.audience) setAudience(prefill.audience);
    if (prefill.desiredOutcome) setDesiredOutcome(prefill.desiredOutcome);
    if (prefill.contentType) setContentType(prefill.contentType);
    if (prefill.platform) setPlatform(prefill.platform);
    if (prefill.additionalContext) setAdditionalContext(prefill.additionalContext);
  }, [location.state]);

  const canProceed = () => {
    if (step === 1) return !!goal && (goal !== "custom" || customGoal.trim().length > 0);
    if (step === 2) return audience.trim().length > 10;
    if (step === 3) return !!desiredOutcome && (desiredOutcome !== "custom" || customOutcome.trim().length > 0);
    if (step === 4) return !!contentType;
    return true;
  };

  const addPromptToAudience = () => {
    if (promptAnswer.trim()) {
      setAudience(prev => prev ? `${prev}\n${promptAnswer.trim()}` : promptAnswer.trim());
      setPromptAnswer("");
    }
    setActivePrompt(null);
  };

  const handleProcess = useCallback(async () => {
    setStep(6);
    setProcessing(true);
    setProcessingMsg(0);

    const interval = setInterval(() => {
      setProcessingMsg(prev => prev < PROCESSING_MESSAGES.length - 1 ? prev + 1 : prev);
    }, 1800);

    try {
      const resp = await api.post("/briefs/process", {
        business_id: businessId,
        strategy_profile_id: strategyProfileId || null,
        answers: {
          goal: effectiveGoal,
          audience,
          desired_outcome: effectiveOutcome,
          content_type: contentType,
          platform: platform || "Any",
          additional_context: additionalContext.trim() || null,
          tone_override: toneOverride || null,
        },
      });
      clearInterval(interval);

      if (resp.data.error) {
        toast.error(resp.data.error_message || "Brief processing failed");
        setStep(5);
      } else {
        try { await api.post("/auth/onboarding/milestones/first_brief_created"); } catch { /* silent */ }
        navigate(`/briefs/${resp.data.brief_id}`);
      }
    } catch (err) {
      clearInterval(interval);
      toast.error(err.response?.data?.detail || "Brief processing failed. Please try again.");
      setStep(5);
    }
    setProcessing(false);
  }, [api, businessId, effectiveGoal, audience, effectiveOutcome, contentType, platform, additionalContext, toneOverride, strategyProfileId, navigate]);

  const goNext = () => {
    if (step === 5) {
      handleProcess();
    } else {
      setStep(s => s + 1);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

        {/* Progress bar (steps 1-5) */}
        {step >= 1 && step <= 5 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate("/generate")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="brief-back-btn">
                <ArrowLeft size={16} /> {step === 1 ? "Back to Generate" : "Back"}
              </button>
              <span className="text-xs text-[var(--text-muted)] font-medium">Step {step} of 5</span>
            </div>
            <div className="flex gap-1.5">
              {[1,2,3,4,5].map(s => (
                <div key={s} className={`flex-1 h-1 rounded-full transition-colors duration-300 ${s <= step ? "bg-[#FF4400]" : "bg-[var(--border)]"}`} />
              ))}
            </div>
            <div className="mt-3"><AlwaysIncludeIndicator /></div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <StepWrapper key="s1" testId="brief-step-goal">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">What do you want this content to accomplish?</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {GOALS.map(g => (
                  <SelectCard key={g.id} selected={goal === g.id} onClick={() => setGoal(g.id)} icon={g.icon} label={g.label} testId={`goal-${g.id}`} />
                ))}
              </div>
              <div className="mt-4">
                <Input value={customGoal} onChange={e => { setCustomGoal(e.target.value); if (e.target.value) setGoal("custom"); }} placeholder="Or type a custom goal..." className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="goal-custom-input" />
              </div>
            </StepWrapper>
          )}

          {step === 2 && (
            <StepWrapper key="s2" testId="brief-step-audience">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">Who is the ideal reader or viewer of this content?</h1>
              <Textarea value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. Small business owners in the trades who are frustrated with inconsistent revenue and want a more predictable pipeline" className="mt-6 min-h-[120px] bg-[var(--bg-card)] border-[var(--border-strong)] resize-y text-sm" data-testid="audience-textarea" />
              <div className="flex flex-wrap gap-2 mt-3">
                {AUDIENCE_PROMPTS.map((p, i) => (
                  <button key={p.chip} onClick={() => setActivePrompt(activePrompt === i ? null : i)} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${activePrompt === i ? "bg-[#FF4400]/10 text-[#FF4400] border-[#FF4400]/30" : "bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)] hover:text-[var(--text-primary)]"}`} data-testid={`audience-prompt-${i}`}>
                    {p.chip}
                  </button>
                ))}
              </div>
              <AnimatePresence>
                {activePrompt !== null && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-3 flex gap-2">
                    <Input value={promptAnswer} onChange={e => setPromptAnswer(e.target.value)} onKeyDown={e => e.key === "Enter" && addPromptToAudience()} placeholder={AUDIENCE_PROMPTS[activePrompt]?.question} className="flex-1 bg-[var(--bg-card)] border-[var(--border-strong)] text-sm h-9" data-testid="audience-prompt-input" />
                    <Button onClick={addPromptToAudience} size="sm" className="bg-[#FF4400] hover:bg-[#FF6633] text-white h-9 px-3"><Plus size={14} /></Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </StepWrapper>
          )}

          {step === 3 && (
            <StepWrapper key="s3" testId="brief-step-outcome">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">What should they do or feel after reading?</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {OUTCOMES.map(o => (
                  <SelectCard key={o.id} selected={desiredOutcome === o.id} onClick={() => setDesiredOutcome(o.id)} icon={o.icon} label={o.label} testId={`outcome-${o.id}`} />
                ))}
              </div>
              <AnimatePresence>
                {desiredOutcome === "custom" && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4">
                    <Input value={customOutcome} onChange={e => setCustomOutcome(e.target.value)} placeholder="Describe the specific action..." className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="outcome-custom-input" />
                  </motion.div>
                )}
              </AnimatePresence>
            </StepWrapper>
          )}

          {step === 4 && (
            <StepWrapper key="s4" testId="brief-step-format">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">What format and where?</h1>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Content Type</label>
                  <div className="flex flex-wrap gap-2">
                    {CONTENT_TYPES.map(ct => (
                      <button key={ct} onClick={() => setContentType(ct)} className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${contentType === ct ? "bg-[#FF4400] text-white border-[#FF4400]" : "bg-[var(--bg-card)] text-[var(--text-secondary)] border-[var(--border)] hover:border-[var(--border-strong)]"}`} data-testid={`ct-${ct.toLowerCase().replace(/[/ ]/g, "-")}`}>
                        {ct}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Platform <span className="normal-case text-[var(--text-muted)]">(optional)</span></label>
                  <Input value={platform} onChange={e => setPlatform(e.target.value)} placeholder="e.g. LinkedIn, Substack, Email List..." className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="platform-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Strategy Profile <span className="normal-case text-[var(--text-muted)]">(optional)</span></label>
                    <button type="button" onClick={() => setShowTemplatePicker(true)} className="text-[11px] text-[#FF4400] hover:text-[#FF6633] font-medium transition-colors" data-testid="brief-start-from-template-btn">
                      Start from template
                    </button>
                  </div>
                  <Select value={strategyProfileId || "__none__"} onValueChange={(v) => setStrategyProfileId(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm">
                      <SelectValue placeholder="Use default business strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Use default business strategy</SelectItem>
                      {strategyProfiles.map((sp) => (
                        <SelectItem key={sp.strategy_profile_id} value={sp.strategy_profile_id}>
                          {sp.name}{sp.strategy_profile_id === defaultStrategyProfileId ? " (Default)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <StrategyTemplatePickerModal
                    isOpen={showTemplatePicker}
                    onClose={() => setShowTemplatePicker(false)}
                    onCloned={(profile) => {
                      loadStrategies();
                      if (profile?.strategy_profile_id) setStrategyProfileId(profile.strategy_profile_id);
                    }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">Tone direction <span className="normal-case text-[var(--text-muted)]">(optional)</span></label>
                  <div className="flex flex-wrap gap-1.5">
                    {TONE_CHIPS.map(t => (
                      <button key={t} onClick={() => setToneOverride(toneOverride === t ? "" : t)} className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${toneOverride === t ? "bg-[#FF4400]/10 text-[#FF4400] border-[#FF4400]/30" : "bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)] hover:text-[var(--text-secondary)]"}`} data-testid={`tone-${t.toLowerCase()}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </StepWrapper>
          )}

          {step === 5 && (
            <StepWrapper key="s5" testId="brief-step-context">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">Anything else the AI should know?</h1>
              <p className="text-sm text-[var(--text-muted)] mt-2">Completely optional — paste in a recent offer, talking points, a stat, competitor positioning, or anything relevant.</p>
              <Textarea value={additionalContext} onChange={e => setAdditionalContext(e.target.value)} placeholder="Paste in a recent offer, a product description, talking points, a stat you want included, competitor positioning to avoid — anything relevant" className="mt-4 min-h-[140px] bg-[var(--bg-card)] border-[var(--border-strong)] resize-y text-sm" data-testid="context-textarea" />
              <button onClick={handleProcess} className="mt-3 text-sm text-[var(--text-muted)] hover:text-[#FF4400] transition-colors" data-testid="skip-context-btn">
                Skip and generate brief
              </button>
            </StepWrapper>
          )}

          {step === 6 && (
            <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-20 space-y-8" data-testid="brief-processing">
              <div className="relative w-20 h-20">
                <SpinnerGap size={80} className="text-[#FF4400] animate-spin" />
              </div>
              <AnimatePresence mode="wait">
                <motion.p key={processingMsg} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="text-base text-[var(--text-secondary)] font-medium">
                  {PROCESSING_MESSAGES[processingMsg]}
                </motion.p>
              </AnimatePresence>
              <div className="flex gap-1.5">
                {PROCESSING_MESSAGES.map((msg, i) => (
                  <div key={msg} className={`w-2 h-2 rounded-full transition-colors duration-300 ${i <= processingMsg ? "bg-[#FF4400]" : "bg-[var(--border)]"}`} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {step >= 1 && step <= 5 && (
          <div className="flex justify-end mt-8">
            <Button onClick={goNext} disabled={!canProceed()} className="bg-[#FF4400] hover:bg-[#FF6633] text-white px-6 h-10 text-sm font-semibold disabled:opacity-40" data-testid="brief-next-btn">
              {step === 5 ? <><Lightning size={16} className="mr-1.5" /> Generate Brief</> : <>Next <ArrowRight size={16} className="ml-1.5" /></>}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

function StepWrapper({ children, testId }) {
  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }} data-testid={testId}>
      {children}
    </motion.div>
  );
}

function SelectCard({ selected, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${selected ? "bg-[#FF4400]/5 border-[#FF4400]/40 shadow-sm" : "bg-[var(--bg-card)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
      <Icon size={20} className={`mt-0.5 shrink-0 ${selected ? "text-[#FF4400]" : "text-[var(--text-muted)]"}`} weight={selected ? "fill" : "regular"} />
      <span className={`text-sm ${selected ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-secondary)]"}`}>{label}</span>
    </button>
  );
}
